import { useMemo } from 'react'

import {
  APPOINTMENT_TYPES_BY_ID,
  type AppointmentActivityActorType,
  type AppointmentActivityChange,
  type AppointmentActivityEntry,
  formatClinicPrice,
  formatName,
  getClinic,
  titleCase,
} from '@snootsvet/shared'

import { FormattedDateTime } from '@/frontend/components/formatted-datetime'
import { getStatusStyle } from '@/frontend/features/appointments/appointments.utils'
import { useListStaff } from '@/frontend/hooks/api/use-list-staff'

const ACTIVITY_LABELS: Record<string, string> = {
  imported_from_digitail: 'Imported from Digitail',
  token_created: 'Booking link sent',
  booked_by_staff: 'Booked by staff',
  booked_by_member: 'Booked by owner',
  used_book_on_day_slot: 'Used book-on-day slot',
  staff_updated_appointment: 'Updated by staff',
  rescheduled_by_member: 'Rescheduled by owner',
  cancelled_by_staff: 'Cancelled by staff',
  cancelled_by_member: 'Cancelled by owner',
  no_show_fee_charged: 'Appointment penalty charged',
  no_show_fee_failed: 'Appointment penalty charge failed',
}

const ACTOR_TYPE_LABELS: Record<AppointmentActivityActorType, string> = {
  staff: 'staff',
  member: 'owner',
}

const CHANGE_FIELD_LABELS: Record<string, string> = {
  status: 'Status',
  startTime: 'Start',
  endTime: 'End',
  clinic: 'Clinic',
  staff: 'Vet',
  appointmentType: 'Type',
}

/**
 * Narrows a metadata entry to a before/after field change, excluding outcome shapes
 * like `noShowFee` that record what happened rather than a value that changed.
 */
function isFieldChange(value: unknown): value is AppointmentActivityChange {
  return !!value && typeof value === 'object' && 'from' in value
}

/**
 * Resolves one side of a field change to a display element. Values are stored as ids
 * (or ISO timestamps) and resolved here: status/clinic/type from static lookups, staff
 * from the active staff list (a vet deactivated since the entry was written falls back
 * to a placeholder), and times rendered as a component so a timestamp change reads at
 * the appointment's clinic with the viewer's own reading annotated.
 */
function ChangeValue({
  field,
  value,
  staffById,
}: {
  field: string
  value: string | null
  staffById: Map<string, string>
}) {
  if (value === null) return <>—</>
  switch (field) {
    case 'status':
      return <>{getStatusStyle(value).label}</>
    case 'startTime':
    case 'endTime':
      return <FormattedDateTime value={value} clinicId={null} />
    case 'clinic':
      return <>{getClinic(value)?.name ?? value}</>
    case 'appointmentType':
      return <>{APPOINTMENT_TYPES_BY_ID[value]?.name ?? titleCase(value)}</>
    case 'staff':
      return <>{staffById.get(value) ?? 'Unknown vet'}</>
    default:
      return <>{value}</>
  }
}

/**
 * Chronological activity timeline for an appointment, rendered newest-first.
 * Unlabelled — callers supply their own heading, since the timeline entry's
 * collapsible trigger already names it.
 */
export function AppointmentActivityLog({
  activityLog,
  clinicId,
}: {
  activityLog: AppointmentActivityEntry[]
  /** The appointment's clinic — its zone fixes when each entry happened. */
  clinicId: string | null
}) {
  const entries = [...activityLog].reverse()

  // Resolve staff-id changes (metadata.staff) to vet names. Only active staff are
  // returned, so a vet deactivated since an entry was written falls back in ChangeValue.
  const { data: staff = [] } = useListStaff()
  const staffById = useMemo(() => new Map(staff.map((s) => [s.id, formatName(s)])), [staff])

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        History
      </span>
      {entries.map((entry, i) => {
        return (
          // Timestamp + actor isn't guaranteed unique (two events can share a ms),
          // so disambiguate with the index.
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: entries in the same second by the same actor need index disambiguation
            key={`${entry.timestamp}-${entry.actor?.id ?? 'system'}-${i}`}
            className="flex gap-2 text-sm"
          >
            <div className="mt-1.5 size-1.5 rounded-full bg-foreground shrink-0" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium">{ACTIVITY_LABELS[entry.event] ?? entry.event}</span>
                <span className="text-xs text-muted-foreground">
                  <FormattedDateTime value={entry.timestamp} clinicId={null} />
                </span>
              </div>
              {entry.actor && (
                <p className="text-muted-foreground text-sm">
                  {formatName(entry.actor)}{' '}
                  <span className="opacity-60">({ACTOR_TYPE_LABELS[entry.actor.type]})</span>
                </p>
              )}
              {entry.event === 'used_book_on_day_slot' && (
                <p className="text-sm text-muted-foreground">
                  Appointment start:{' '}
                  <FormattedDateTime
                    value={entry.metadata.bookOnDay.startTime}
                    clinicId={entry.metadata.bookOnDay.clinicId}
                  />
                  {' · '}
                  {entry.metadata.bookOnDay.lead_time_hours.toFixed(1)} hours ahead
                </p>
              )}
              {(entry.event === 'no_show_fee_charged' || entry.event === 'no_show_fee_failed') && (
                <p className="text-sm text-muted-foreground">
                  {clinicId && formatClinicPrice(entry.metadata.noShowFee.amount, clinicId)}
                  {entry.metadata.noShowFee.reason &&
                    (clinicId ? ' — ' : '') + entry.metadata.noShowFee.reason}
                </p>
              )}
              {(entry.event === 'staff_updated_appointment' ||
                entry.event === 'rescheduled_by_member') &&
                Object.entries(entry.metadata).map(([field, change]) =>
                  isFieldChange(change) ? (
                    <p key={field} className="text-sm text-muted-foreground">
                      {CHANGE_FIELD_LABELS[field] ?? field}:{' '}
                      <ChangeValue field={field} value={change.from} staffById={staffById} /> →{' '}
                      <ChangeValue field={field} value={change.to} staffById={staffById} />
                    </p>
                  ) : null
                )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
